/**
 *  CampaignDetailSidebar script file
**/
import * as d3 from 'd3'
import filters from '@/filters/customFormatters'
import { CHANNEL_LABELS, CHANNEL_COLORS, CHANNEL_OPTIONS, CAMPAIGN_STATUSES, CHANNEL_PRODUCT_TYPES } from '@/utils/campaignOptions'
import channelService from '@/api/channelService'
import moment from 'moment'
import layerService from '@/api/layerService'
import locationsService from '@/api/locationsService'
import buildQuery from '@/utils/buildQuery'
import downloadBlob from '@/utils/downloadBlob'
import authService, { USER_ROLES } from '@/api/authService'
import { IconLayer, ScatterplotLayer } from '@deck.gl/layers'
import { ADVERT_TYPES, advertIconUrl, advertSquareIconUrl, poiIconUrl } from '@/utils/mapUtils'
import { EXCEL_MIME_TYPE } from '@/utils/csvUtils'
import i18n from '@/i18n'

const AREA_COLOR = [22, 150, 157, 1]
const AREA_HOVER_COLOR = [22, 150, 157, 75]
const AREA_SELECTED_COLOR = [22, 150, 157, 140]
const AREA_BORDER_COLOR = [22, 150, 157, 255]

function getAreaColor (id, hover, selection) {
  const hovered = id === hover
  const allSelected = selection.length === 1 && selection[0].id === 'all'
  const selected = allSelected || selection.some(s => s.id === id)
  if (selected) {
    return AREA_SELECTED_COLOR
  }
  if (hovered) {
    return AREA_HOVER_COLOR
  }
  return AREA_COLOR
}

function getAreaTooltip (object) {
  const text = object.label || object.name || object.address
  const style = {
    'background': 'var(--colorNegative)',
    color: 'var(--colorHigh)'
  }
  return { text, style, className: 'map-popup-white' }
}

export default {
  name: 'CampaignDetailSidebar',
  mixins: [filters],
  components: {
    TagListSelector: () => import(/* webpackChunkName: "TagListSelector" */ '@/components/tag-list-selector/TagListSelector.vue')
  },
  props: {
    expanded: {
      type: Boolean,
      default: false
    },
    campaign: {
      type: Object,
      required: true
    }
  },
  data () {
    return {
      EXCEL_MIME_TYPE: EXCEL_MIME_TYPE,
      authData: authService.authData,
      uploadError: false,
      uploadSuccess: false,
      selectedChannel: { type: 'GOOGLESEARCHADS' },
      compactHeader: false,
      kpiSelectedValues: {},
      activeKpi: 'active',
      kpis: {
        metrics: [],
        zones: []
      },
      areasHelp: {
        title: i18n.t('AREAS_HELP_TITLE'),
        text: i18n.t('AREAS_HELP_BODY')
      },
      selectedAreas: [],
      hoveredAreaId: null,
      selectedZones: [],
      hoveredZoneId: null,
      poiLayerId: 'campaing-details-pois',
      advertLayerId: 'campaing-details-adverts',
      zoneLayerId: 'campaing-details-zones',
      bufferLayerId: 'campaing-details-buffers'
    }
  },
  watch: {
    hoveredAreaId () {
      this.updateBuffers()
    },
    selectedAreas () {
      this.updateBuffers()
      this.fetchKPIs()
    },
    hoveredZoneId () {
      this.updateZoneLayer()
    },
    selectedZones () {
      this.updateZoneLayer()
      this.fetchKPIs()
    }
  },
  computed: {
    campaignType () {
      return this.selectedChannel.direction && CHANNEL_PRODUCT_TYPES[this.selectedChannel.direction].label
    },
    canUploadResults () {
      return this.authData.user.role === USER_ROLES.AGENCY || this.authData.user.role === USER_ROLES.ROOT
    },
    showResults () {
      return this.campaign.status === CAMPAIGN_STATUSES.ACTIVE || this.campaign.status === CAMPAIGN_STATUSES.FINISHED
    },
    channelsWithOptions () {
      return this.campaign.channels.map(c => {
        const label = CHANNEL_LABELS[c.type]
        const color = CHANNEL_COLORS[c.type]
        return { ...c, label, color }
      })
    },
    kpiChartData () {
      return this.kpis.metrics.map(kpi => ({ name: kpi.key, values: kpi.data }))
    },
    selectedChannelColor () {
      return this.selectedChannelModel && this.selectedChannelModel.color
    },
    selectedChannelModel () {
      const channel = this.channelsWithOptions.find(c => c.type === this.selectedChannel.type)
      return channel
    },
    year () {
      return new Date().getFullYear()
    },
    dateDiff () {
      if (!this.selectedChannelModel) {
        return 0
      }
      const start = moment(this.selectedChannelModel.start_date)
      const finish = moment(this.selectedChannelModel.finish_date)
      return finish.diff(start, 'days')
    },
    channelFiles () {
      return Array.isArray(this.selectedChannelModel.creativity) ? this.selectedChannelModel.creativity : []
    },
    selectedAreaType () {
      if (!this.selectedChannelModel) {
        return null
      }
      if (this.selectedChannelModel.zones && this.selectedChannelModel.zones.length > 0) {
        return 'zone'
      }
      if (this.selectedChannelModel.shops && this.selectedChannelModel.shops.length > 0) {
        return 'center'
      }
      if (this.selectedChannelModel.pois && this.selectedChannelModel.pois.length > 0) {
        return 'poi'
      }
      if (this.selectedChannelModel.adverts && this.selectedChannelModel.adverts.length > 0) {
        return 'advert'
      }
    },
    selectedAreaLabel () {
      const singluar = {
        advert: i18n.t('ADVERT'),
        poi: i18n.t('POI'),
        center: i18n.t('CENTER'),
        zone: i18n.tc('ZIPCODE', 1)
      }
      const plural = {
        advert: i18n.t('ADVERTS'),
        poi: i18n.t('POIS'),
        center: i18n.t('CENTERS'),
        zone: i18n.tc('ZIPCODE', 2)
      }
      const mapping = this.areas.length === 1 ? singluar : plural
      return mapping[this.selectedAreaType]
    },
    fullAreas () {
      if (!this.selectedChannelModel) {
        return []
      }
      if (this.selectedAreaType === 'zone') {
        return this.selectedChannelModel.zones
      }
      if (this.selectedAreaType === 'center') {
        return this.selectedChannelModel.shops
      }
      if (this.selectedAreaType === 'poi') {
        return this.selectedChannelModel.pois
      }
      if (this.selectedAreaType === 'advert') {
        return this.selectedChannelModel.adverts
      }
      return []
    },
    areas () {
      return this.selectedAreaType === 'zone'
        ? this.fullAreas
        : this.fullAreas.map(a => ({ ...a.customer_region, id: a.id }))
    },
    selectedRadius () {
      return Number(this.fullAreas.length && this.fullAreas[0].radius)
    },
    // zoneOptions () {
    //   return [{ ...this.allAreaOption }].concat(this.areas)
    // },
    areaOptions () {
      return [{ ...this.allAreaOption }].concat(this.areas)
    },
    allAreaOption () {
      return {
        id: 'all',
        name: `${i18n.t('ALL_THE')} ${this.selectedAreaLabel}`
      }
    }
  },
  mounted () {
    if (this.campaign.channels.length) {
      this.toggleTag({ id: this.campaign.channels[0].type })
    }
  },
  beforeDestroy () {
    layerService.removeLayer(this.advertLayerId)
    layerService.removeLayer(this.bufferLayerId)
    layerService.removeLayer(this.poiLayerId)
    layerService.removeLayer(this.zoneLayerId)
  },
  methods: {
    updateZoneLayer () {
      layerService.updateLayer(this.zoneLayerId, {
        updateTriggers: {
          getFillColor: [this.hoveredZoneId, this.selectedZones]
        }
      })
    },
    resetSelectedZones () {
      this.selectedZones = [{ ...this.allAreaOption }]
    },
    resetSelectedAreas () {
      this.selectedAreas = [{ ...this.allAreaOption }]
    },
    async drawAreaLayer () {
      if (this.selectedAreaType === 'zone') {
        this.resetSelectedZones()
      } else {
        this.resetSelectedAreas()
      }

      if (this.selectedAreaType !== 'zone') {
        this.drawBuffers()
      }

      this.toggleLayers()

      if (this.selectedAreaType === 'zone') {
        const data = await locationsService.getPostalCodes({
          // id__in: this.areas.map(a => a.id),
          // in_shop_influence_area: this.campaign.shops.map(s => s.id),
          campaign_channel_id: this.selectedChannel.id,
          query: 1,
          page_size: 1
        })
        await layerService.addSQLLayer({
          id: this.zoneLayerId,
          sql: buildQuery(data.query),
          zIndex: -1,
          pickable: true,
          updateTriggers: {
            getFillColor: [this.hoveredZoneId, this.selectedZones]
          },
          getLineColor: AREA_BORDER_COLOR,
          getFillColor: (object) => {
            const id = object.properties.cartodb_id
            return getAreaColor(id, this.hoveredZoneId, this.selectedZones)
          },
          getTooltip ({ object }) {
            const text = `${i18n.t('ZIPCODE_SHORTER')} ${object.properties.code}`
            const style = {
              'background': 'var(--colorNegative)',
              color: 'var(--colorHigh)'
            }
            return { text, style, className: 'map-popup-white' }
          },
          onHover: ({ object }) => {
            this.hoveredZoneId = (object && object.properties.cartodb_id) || null
          },
          onClick: ({ object }) => {
            const { name, label, address, cartodb_id: id } = object.properties
            const elem = {
              id,
              name: label || name || address
            }
            this.onZoneSelected(elem)
          }
        })
      }
      if (this.selectedAreaType === 'advert') {
        layerService.addLayer({
          id: this.advertLayerId,
          data: this.areas,
          layerType: IconLayer,
          getPosition: d => [d.lng, d.lat],
          getIcon: (d) => ({
            width: 40,
            height: 40,
            url: d.properties.advert_type === ADVERT_TYPES.PRODUCT ? advertIconUrl : advertSquareIconUrl
          })
        })
      }
      if (this.selectedAreaType === 'poi') {
        layerService.addLayer({
          id: this.poiLayerId,
          data: this.areas,
          layerType: IconLayer,
          getSize: 32,
          getPosition: d => [d.lng, d.lat],
          getIcon: () => ({
            width: 40,
            height: 40,
            url: poiIconUrl
          })
        })
      }
    },
    updateBuffers () {
      layerService.updateLayer(this.bufferLayerId, {
        updateTriggers: {
          getFillColor: [this.hoveredAreaId, this.selectedAreas]
        }
      })
    },
    drawBuffers () {
      // TODO: pick circle color from the color palette
      // depdending on the position of the point in the scale of the selected KPI
      layerService.addLayer({
        id: this.bufferLayerId,
        layerType: ScatterplotLayer,
        data: this.areas,
        getPosition: d => [d.lng, d.lat],
        getRadius: this.selectedRadius,
        stroked: true,
        filled: true,
        pickable: true,
        lineWidthMinPixels: 2,
        zIndex: -1,
        updateTriggers: {
          getFillColor: [this.hoveredAreaId, this.selectedAreas]
        },
        getLineColor: AREA_BORDER_COLOR,
        getFillColor: (object) => {
          const id = object.id
          return getAreaColor(id, this.hoveredAreaId, this.selectedAreas)
        },
        getTooltip: ({ object }) => getAreaTooltip(object),
        onHover: ({ object }) => {
          this.hoveredAreaId = (object && object.id) || null
        },
        onClick: ({ object }) => {
          const { name, label, address, id } = object
          const elem = {
            id,
            name: label || name || address
          }
          this.onAreaSelected(elem)
        }
      })
    },
    onAreaSelected (opt) {
      if (this.selectedAreas.some(a => a.id === opt.id)) {
        this.selectedAreas = this.selectedAreas.filter(a => a.id !== opt.id)
      } else {
        if (opt.id === 'all') {
          this.selectedAreas = [{ ...this.allAreaOption }]
        } else {
          this.selectedAreas = this.selectedAreas.filter(opt => opt.id !== 'all').concat(opt)
        }
      }
    },
    onZoneSelected (opt) {
      if (this.selectedZones.some(a => a.id === opt.id)) {
        this.selectedZones = this.selectedZones.filter(a => a.id !== opt.id)
      } else {
        if (opt.id === 'all') {
          this.selectedZones = [{ ...this.allAreaOption }]
        } else {
          this.selectedZones = this.selectedZones.filter(opt => opt.id !== 'all').concat(opt)
        }
      }
    },
    toggleLayers () {
      const mapping = {
        'advert': this.advertLayerId,
        'poi': this.poiLayerId,
        'zone': this.zoneLayerId
      }
      if (this.selectedAreaType === 'zone') {
        layerService.hideLayer(this.bufferLayerId)
      }
      for (const key in mapping) {
        if (this.selectedAreaType === key) {
          layerService.showLayer(mapping[key])
        } else {
          layerService.hideLayer(mapping[key])
        }
      }
    },
    async fetchKPIs () {
      const channel = this.campaign.channels.find(c => c.type === this.selectedChannel.type)
      if (!channel) {
        return
      }
      const areas = this.selectedAreaType === 'zone' ? this.selectedZones : this.selectedAreas
      if (areas.length === 0) {
        this.kpis = {
          count: 0,
          budget: 0,
          metrics: this.kpis.metrics.map(d => ({ ...d, total: 0, data: [] })),
          zones: []
        }
      } else {
        const ids = areas.map(a => a.id).filter(id => id !== 'all')
        const kpis = await channelService.getChannelStats(channel.id, channel.type, ids)
        this.kpis = kpis
      }
      this.$nextTick(() => {
        this.$refs.areachart && this.$refs.areachart.forEach(ref => ref.clearChart())
      })
    },
    toggle () {
      this.$emit('toggle')
    },
    async toggleTag ({ id }) {
      this.selectedChannel = this.campaign.channels.find(c => c.type === id)
      if (this.$refs.scrollbar) {
        this.$refs.scrollbar.scrollTo(0, 0)
      }
      await this.drawAreaLayer()
      await this.$nextTick()
    },
    onScroll (scroll) {
      if (scroll.y > 10) {
        this.compactHeader = true
      } else {
        this.compactHeader = false
      }
    },
    setActiveKpi () {
      // TODO
    },
    onAreaChartMouseMove (data) {
      const bisectDate = d3.bisector(d => d.date).left
      this.kpis.metrics.forEach((kpi, kpiIndex) => {
        const dataIndex = bisectDate(kpi.data, data.date)
        const value = kpi.data[dataIndex].value
        // TODO: change this, store only the index and derive everything else in a computed
        this.$set(this.kpiSelectedValues, kpi.key, value)

        if (data.kpi !== kpi.key) {
          this.$refs.areachart[kpiIndex].drawCrosshair(data.date)
        }
      })
    },
    onAreaChartMouseOut (data) {
      this.kpiSelectedValues = {}
      this.kpis.metrics.forEach((kpi, kpiIndex) => {
        if (data !== kpi.key) {
          this.$refs.areachart[kpiIndex].hideCrosshair()
        }
      })
    },
    toggleRanking () {
      // TODO
    },
    getKpiChartData (index) {
      return [this.kpiChartData[index] || { name: 'N/A', values: [] }]
    },
    getTargetColor (value) {
      if (value === 'F') {
        return 'var(--colorFemale)'
      }
      if (value === 'M') {
        return 'var(--colorMale)'
      }
      return 'var(--colorPrimary)'
    },
    isMediumConfirmed (channel) {
      return channel.type === CHANNEL_OPTIONS.FACEBOOK ||
        channel.type === CHANNEL_OPTIONS.INSTAGRAM ||
        channel.type === CHANNEL_OPTIONS.YOUTUBE ||
        channel.type === CHANNEL_OPTIONS.LOCALINVENTORYADS ||
        channel.type === CHANNEL_OPTIONS.GEOLOCATION ||
        channel.type === CHANNEL_OPTIONS.GOOGLESEARCHADS ||
        channel.type === CHANNEL_OPTIONS.META ||
        channel.type === CHANNEL_OPTIONS.GOOGLE ||
        channel.type === CHANNEL_OPTIONS.RETARGETING ||
        channel.type === CHANNEL_OPTIONS.DV360 ||
        channel.sonata_status === 'active' ||
        channel.sonata_status === 'finished'
    },
    getConfirmationIcon (channel) {
      return this.isMediumConfirmed(channel)
        ? this.selectedChannel.type === channel.type
          ? 'icon-tick-circle'
          : 'icon-tick-circle-positive'
        : this.selectedChannel.type === channel.type
          ? 'icon-clock'
          : 'icon-clock-positive'
    },
    downloadResultTemplate () {
      channelService.getResultsCSV()
        .then(data => {
          downloadBlob(data, EXCEL_MIME_TYPE, 'results_example.xlsx')
        })
    },
    onFileUpload (file) {
      channelService.uploadResultsCSV(this.selectedChannel.id, this.selectedChannel.type, file)
        .then(() => {
          this.$refs.results.clear()
          this.uploadSuccess = true
          setTimeout(() => {
            this.uploadSuccess = false
          }, 1500)
        }).catch(() => {
          this.$refs.results.clear()
          this.uploadError = true
          setTimeout(() => {
            this.uploadError = false
          }, 1500)
        })
    },
    getSelectedKPIValue ({ key, total }) {
      const value = this.kpiSelectedValues[key]
      return (value || value === 0) ? value : total
    }
  },
  filters: {
    formatDate (date) {
      return new Date(date).toLocaleDateString()
    }
  }
}
